import { atom } from "jotai";

// Sample bundles: a set of swatches an interior designer sends to their client,
// billed to the designer's brand. Placed through the internal-order pipeline, so
// the option sets and quote shape are the internal-order ones.

export type SampleBundleStatus = 'DRAFT' | 'SUBMITTED' | 'FAILED';

// #region ISampleBundleItem
export interface ISampleBundleItem {
    id?: number;
    bundle_id?: number;
    design_id: number;
    material_id?: number | null;
    scale?: string | null;
    quantity: number;
    title?: string | null;
    thumbnail_url?: string | null;
    pattern_type?: string | null;
}

// #region ISampleBundleTracking
export interface ISampleBundleTracking {
    tracking_number: string | null;
    tracking_url: string | null;
}

// #region ISampleBundle
export interface ISampleBundle {
    id?: number;
    designer_brand_id?: number;
    interior_project_id?: number | null;

    client_name: string;
    client_email?: string | null;

    ship_address_1: string;
    ship_address_2?: string | null;
    ship_city: string;
    ship_state: string;
    ship_zip: string;
    ship_country: string;

    default_material_id?: number | null;
    default_scale?: string | null;

    status: SampleBundleStatus;
    order_number?: number | null;
    queue_id?: number | null;

    created_at?: string;
    updated_at?: string;

    items: ISampleBundleItem[];
    item_count?: number;
    order_status?: string | null;
    tracking?: ISampleBundleTracking[];
}

// #region ISampleBundleOptions
// Mirrors the backend InternalOrderOptionsDTO — the bundle form and the internal
// order builder read the same option sets so they cannot drift.
export interface ISampleBundleOptions {
    material_types: string[];
    valid_heights_ft: number[];
    repeat_scale_sizes: string[];
    pattern_types: string[];
    sample_methods: string[];
    mural_sizings: string[];
    max_line_items: number;
}

// #region ISampleBundlePrefillDesign
// Display fields for a prefilled design. Line items are rebuilt server-side at
// submit time, so these are for the bundle form only.
export interface ISampleBundlePrefillDesign {
    design_id: number;
    title: string | null;
    thumbnail_url: string | null;
    pattern_type: string | null;
}

// #region ISampleBundlePrefill
export interface ISampleBundlePrefill {
    interior_project_id: number;
    client_name: string;
    client_email: string | null;
    design_ids: number[];
    // Optional: older backends return only design_ids.
    designs?: ISampleBundlePrefillDesign[];
    // The project's stored client ship-to. Optional for the same reason.
    ship_address_1?: string | null;
    ship_address_2?: string | null;
    ship_city?: string | null;
    ship_state?: string | null;
    ship_zip?: string | null;
    ship_country?: string | null;
    ship_phone?: string | null;
}

// #region ISampleBundleDraftItem
export interface ISampleBundleDraftItem {
    design_id: number;
    title?: string | null;
    thumbnail_url?: string | null;
    pattern_type?: string | null;
    material_id?: number | null;
    scale?: string | null;
    quantity: number;
}

// #region ISampleBundleDraft
export interface ISampleBundleDraft {
    interior_project_id?: number | null;
    client_name: string;
    client_email: string;
    ship_address_1: string;
    ship_address_2: string;
    ship_city: string;
    ship_state: string;
    ship_zip: string;
    ship_country: string;
    default_material_id: number | null;
    default_scale: string | null;
    items: ISampleBundleDraftItem[];
    notes?: string;
}

// #region ISampleBundleQuote
// The internal-order quote snapshot, narrowed to the fields the bundle summary
// renders. Kept structural so it stays compatible with the internal-order type.
export interface ISampleBundleQuote {
    materials_total: number;
    box_total: number;
    shipping_total: number;
    taxes: number;
    duties: number;
    total: number;
    rate_source?: 'flat' | 'shipstation';
    line_items?: { name: string; quantity: number; unit_cost?: number; line_total?: number; is_sample?: boolean }[];
}

// #region ISampleBundleCreateResult
export interface ISampleBundleCreateResult {
    bundle: ISampleBundle;
    order_number: number;
    quote: ISampleBundleQuote | null;
}

// #region EMPTY_SAMPLE_BUNDLE_DRAFT
export const EMPTY_SAMPLE_BUNDLE_DRAFT: ISampleBundleDraft = {
    client_name: '',
    client_email: '',
    ship_address_1: '',
    ship_address_2: '',
    ship_city: '',
    ship_state: '',
    ship_zip: '',
    ship_country: 'US',
    default_material_id: null,
    default_scale: null,
    items: []
};

// Bundle history list.
export const sampleBundlesAtom = atom<ISampleBundle[]>([]);
export const sampleBundlesTotalAtom = atom<number>(0);
export const sampleBundlesLoadingAtom = atom<boolean>(false);

// The in-progress bundle. Lives in an atom so the catalog multi-select and the
// create page can hand designs to each other across a route change.
export const sampleBundleDraftAtom = atom<ISampleBundleDraft>({ ...EMPTY_SAMPLE_BUNDLE_DRAFT });
export const sampleBundleQuoteAtom = atom<ISampleBundleQuote | null>(null);
export const sampleBundleSubmittingAtom = atom<boolean>(false);
